import { useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';

const AnimatedBackground = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  const { theme } = useTheme();

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Enhanced particles array with more properties
    const particles: Array<{
      x: number;
      y: number;
      z: number;
      vx: number;
      vy: number;
      vz: number;
      size: number;
      opacity: number;
      rotation: number;
      rotationSpeed: number;
      pulsePhase: number;
      pulseSpeed: number;
    }> = [];

    // Create enhanced particles
    for (let i = 0; i < 150; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        z: Math.random() * 1000,
        vx: (Math.random() - 0.5) * 0.8,
        vy: (Math.random() - 0.5) * 0.8,
        vz: (Math.random() - 0.5) * 2,
        size: Math.random() * 3 + 1,
        opacity: Math.random() * 0.6 + 0.2,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 0.02,
        pulsePhase: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.02 + 0.01
      });
    }

    const animate = () => {
      // Clear canvas with theme-aware background
      if (theme === 'light') {
        // Light theme: clean white to light gray gradient
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradient.addColorStop(0, 'rgba(248, 250, 252, 1)'); // slate-50
        gradient.addColorStop(0.5, 'rgba(241, 245, 249, 1)'); // slate-100
        gradient.addColorStop(1, 'rgba(226, 232, 240, 1)'); // slate-200
        ctx.fillStyle = gradient;
      } else {
        // Dark theme: original dark gradient
        const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
        gradient.addColorStop(0, 'rgba(15, 23, 42, 1)'); // slate-900
        gradient.addColorStop(0.5, 'rgba(30, 41, 59, 1)'); // slate-800
        gradient.addColorStop(1, 'rgba(51, 65, 85, 1)'); // slate-700
        ctx.fillStyle = gradient;
      }
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Update and draw particles
      particles.forEach((particle, index) => {
        // Update positions
        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.z += particle.vz;
        particle.rotation += particle.rotationSpeed;
        particle.pulsePhase += particle.pulseSpeed;

        // Wrap around edges
        if (particle.x < 0) particle.x = canvas.width;
        if (particle.x > canvas.width) particle.x = 0;
        if (particle.y < 0) particle.y = canvas.height;
        if (particle.y > canvas.height) particle.y = 0;
        if (particle.z < 0) particle.z = 1000;
        if (particle.z > 1000) particle.z = 0;

        // Calculate 3D perspective
        const perspective = 800;
        const scale = perspective / (perspective + particle.z);
        const x2d = particle.x * scale + canvas.width * (1 - scale) / 2;
        const y2d = particle.y * scale + canvas.height * (1 - scale) / 2;
        const size = particle.size * scale;

        // Calculate pulsing opacity
        const pulseOpacity = particle.opacity * (0.8 + 0.4 * Math.sin(particle.pulsePhase));
        const finalOpacity = Math.max(0.1, Math.min(0.8, pulseOpacity * scale));

        // Theme-aware particle colors
        if (theme === 'light') {
          // Light theme: subtle blue and purple particles
          const hue = (index * 137.5) % 360; // Golden angle for color distribution
          if (hue < 120) {
            ctx.fillStyle = `hsla(${210 + (hue % 30)}, 65%, 60%, ${finalOpacity * 0.6})`;
          } else if (hue < 240) {
            ctx.fillStyle = `hsla(${240 + (hue % 30)}, 70%, 65%, ${finalOpacity * 0.5})`;
          } else {
            ctx.fillStyle = `hsla(${180 + (hue % 30)}, 60%, 70%, ${finalOpacity * 0.4})`;
          }
        } else {
          // Dark theme: vibrant particles
          const hue = (index * 137.5) % 360;
          if (hue < 120) {
            ctx.fillStyle = `hsla(${200 + (hue % 60)}, 70%, 60%, ${finalOpacity})`;
          } else if (hue < 240) {
            ctx.fillStyle = `hsla(${280 + (hue % 60)}, 80%, 70%, ${finalOpacity})`;
          } else {
            ctx.fillStyle = `hsla(${160 + (hue % 60)}, 60%, 80%, ${finalOpacity})`;
          }
        }

        // Draw particle with rotation
        ctx.save();
        ctx.translate(x2d, y2d);
        ctx.rotate(particle.rotation);
        ctx.fillRect(-size/2, -size/2, size, size);
        ctx.restore();

        // Enhanced connection lines between nearby particles
        for (let j = index + 1; j < particles.length; j++) {
          const other = particles[j];
          const dx = particle.x - other.x;
          const dy = particle.y - other.y;
          const dz = particle.z - other.z;
          const distance3D = Math.sqrt(dx * dx + dy * dy + dz * dz * 0.1);

          if (distance3D < 120) {
            const otherScale = perspective / (perspective + other.z);
            const otherX2d = other.x * otherScale + canvas.width * (1 - otherScale) / 2;
            const otherY2d = other.y * otherScale + canvas.height * (1 - otherScale) / 2;

            const lineOpacity = (1 - distance3D / 120) * 0.3 * Math.min(scale, otherScale);
            
            if (theme === 'light') {
              ctx.strokeStyle = `rgba(59, 130, 246, ${lineOpacity * 0.4})`; // blue-500 with reduced opacity
            } else {
              ctx.strokeStyle = `rgba(147, 197, 253, ${lineOpacity})`; // blue-300
            }
            
            ctx.lineWidth = 0.8;
            ctx.beginPath();
            ctx.moveTo(x2d, y2d);
            ctx.lineTo(otherX2d, otherY2d);
            ctx.stroke();
          }
        }
      });

      animationRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [theme]); // Add theme as dependency to re-run when theme changes

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 w-full h-full -z-10"
      style={{ background: 'transparent' }}
    />
  );
};

export default AnimatedBackground;
